/*
 * Kitchen Cabinet — Shop By Category
 * ---------------------------------------------------------------------------
 * Vanilla JS, no dependencies. Scoped to .kitchen-cabinet-catalog roots only.
 *
 * Reuses the theme's own cart plumbing when it exists:
 *   window.routes.cart_add_url, <cart-drawer> / <cart-notification>,
 *   getSectionsToRender() + renderContents() (same contract as product-form.js),
 *   publish() / PUB_SUB_EVENTS for cart subscribers.
 * Falls back to the plain AJAX Cart API when none of that is present.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '.kitchen-cabinet-catalog';

  function KitchenCabinetCatalog(root) {
    this.root = root;
    this.status = root.querySelector('[data-kcc-status]');
    this.styleSelect = root.querySelector('[data-kcc-style-select]');
    this.statusTimer = null;
    this.searchTimer = null;
    this.searchTerm = '';

    // Category tiles are merchant-defined, so the starting filter comes from
    // whichever button was rendered active rather than a hardcoded 'all'.
    var initial = root.querySelector('[data-kcc-filter].is-active');
    this.activeFilter = initial ? initial.getAttribute('data-kcc-filter') : 'all';

    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onInput = this.onInput.bind(this);

    root.addEventListener('click', this.onClick);
    root.addEventListener('change', this.onChange);
    root.addEventListener('input', this.onInput);

    this.restoreFromUrl();
  }

  /* --- URL state -------------------------------------------------------- */

  KitchenCabinetCatalog.prototype.restoreFromUrl = function () {
    var params = new URLSearchParams(window.location.search);
    var style = params.get('style');
    var type = params.get('type');

    if (style && this.getPanel(style)) {
      this.setStyle(style, false);
    } else {
      this.setStyle(this.root.dataset.defaultStyle || '', false);
    }

    if (type && this.root.querySelector('[data-kcc-filter="' + cssEscape(type) + '"]')) {
      this.setFilter(type, false);
    } else {
      this.applyFilter();
    }
  };

  KitchenCabinetCatalog.prototype.updateUrl = function () {
    if (!window.history || !window.history.replaceState) return;
    var url = new URL(window.location.href);
    var style = this.currentStyle();

    if (style) {
      url.searchParams.set('style', style);
    } else {
      url.searchParams.delete('style');
    }

    if (this.activeFilter && this.activeFilter !== 'all') {
      url.searchParams.set('type', this.activeFilter);
    } else {
      url.searchParams.delete('type');
    }

    window.history.replaceState({}, '', url.toString());
  };

  /* --- Style panels ----------------------------------------------------- */

  KitchenCabinetCatalog.prototype.getPanel = function (style) {
    if (!style) return null;
    return this.root.querySelector('[data-kcc-style="' + cssEscape(style) + '"]');
  };

  KitchenCabinetCatalog.prototype.currentStyle = function () {
    var panel = this.root.querySelector('[data-kcc-style]:not([hidden])');
    return panel ? panel.getAttribute('data-kcc-style') : '';
  };

  KitchenCabinetCatalog.prototype.setStyle = function (style, pushUrl) {
    var panel = this.getPanel(style);
    if (!panel) return;

    this.root.querySelectorAll('[data-kcc-style]').forEach(function (el) {
      el.hidden = el !== panel;
    });

    if (this.styleSelect && this.styleSelect.value !== style) {
      this.styleSelect.value = style;
    }

    this.applyFilter();
    if (pushUrl !== false) this.updateUrl();
  };

  /* --- Cabinet type filter ---------------------------------------------- */

  KitchenCabinetCatalog.prototype.setFilter = function (type, pushUrl) {
    this.activeFilter = type || 'all';

    this.root.querySelectorAll('[data-kcc-filter]').forEach(
      function (button) {
        var isActive = button.getAttribute('data-kcc-filter') === this.activeFilter;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      }.bind(this)
    );

    this.applyFilter();
    if (pushUrl !== false) this.updateUrl();
  };

  KitchenCabinetCatalog.prototype.applyFilter = function () {
    var panel = this.root.querySelector('[data-kcc-style]:not([hidden])');
    if (!panel) return;

    var type = this.activeFilter;
    var term = this.searchTerm;
    var visibleRows = 0;

    panel.querySelectorAll('[data-kcc-group]').forEach(function (group) {
      var groupVisible = 0;

      group.querySelectorAll('[data-kcc-row]').forEach(function (row) {
        var types = (row.getAttribute('data-cabinet-type') || '')
          .toLowerCase()
          .split(/[\s,|]+/)
          .filter(Boolean);
        var match = type === 'all' || types.indexOf(type) !== -1;

        if (match && term) {
          match = (row.getAttribute('data-search') || '').indexOf(term) !== -1;
        }

        row.hidden = !match;
        if (match) groupVisible++;
      });

      group.hidden = groupVisible === 0;
      visibleRows += groupVisible;
    });

    var noResults = panel.querySelector('[data-kcc-no-results]');
    if (noResults) noResults.hidden = visibleRows !== 0;
  };

  /* --- Quantity --------------------------------------------------------- */

  KitchenCabinetCatalog.prototype.stepQuantity = function (button) {
    var wrap = button.closest('[data-kcc-qty]');
    if (!wrap) return;
    var input = wrap.querySelector('[data-kcc-qty-input]');
    if (!input) return;

    var step = parseInt(button.getAttribute('data-kcc-qty-step'), 10) || 0;
    var value = parseInt(input.value, 10);
    if (isNaN(value)) value = 1;

    input.value = Math.max(1, value + step);
  };

  KitchenCabinetCatalog.prototype.normalizeQuantity = function (input) {
    var value = parseInt(input.value, 10);
    if (isNaN(value) || value < 1) return;
    input.value = value;
  };

  /* --- Variant switching ------------------------------------------------ */

  KitchenCabinetCatalog.prototype.selectVariant = function (select) {
    var row = select.closest('[data-kcc-row]');
    if (!row) return;

    var option = select.options[select.selectedIndex];
    if (!option) return;

    var price = row.querySelector('[data-kcc-price]');
    var compare = row.querySelector('[data-kcc-compare]');
    var model = row.querySelector('[data-kcc-model]');
    var button = row.querySelector('[data-kcc-add]');
    var available = option.getAttribute('data-available') === 'true';

    if (price) price.textContent = option.getAttribute('data-price') || '';

    if (compare) {
      var compareValue = option.getAttribute('data-compare');
      compare.innerHTML = '';
      if (compareValue) {
        var strike = document.createElement('s');
        strike.textContent = compareValue;
        compare.appendChild(strike);
      }
    }

    if (model && option.getAttribute('data-model')) {
      model.textContent = option.getAttribute('data-model');
    }

    if (button) {
      button.setAttribute('data-variant-id', option.value);
      button.disabled = !available;
      button.classList.toggle('kcc__atc--sold-out', !available);
      if (available) {
        button.removeAttribute('aria-disabled');
      } else {
        button.setAttribute('aria-disabled', 'true');
      }

      var text = button.querySelector('[data-kcc-add-text]');
      if (text) {
        if (!button.dataset.defaultLabel) button.dataset.defaultLabel = text.textContent.trim();
        text.textContent = available
          ? button.dataset.defaultLabel
          : (window.variantStrings && window.variantStrings.soldOut) || 'Sold Out';
      }
    }

    row.querySelectorAll('[data-kcc-qty] button, [data-kcc-qty] input').forEach(function (el) {
      el.disabled = !available;
    });
  };

  /* --- Add to cart ------------------------------------------------------ */

  KitchenCabinetCatalog.prototype.addToCart = function (button) {
    if (button.classList.contains('is-loading') || button.disabled) return;

    var row = button.closest('[data-kcc-row]');
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    var quantityInput = row ? row.querySelector('[data-kcc-qty-input]') : null;
    var quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
    if (isNaN(quantity) || quantity < 1) quantity = 1;
    if (quantityInput) quantityInput.value = quantity;

    this.setLoading(button, true);
    this.setStatus('', null);

    var cart =
      document.querySelector('cart-notification') || document.querySelector('cart-drawer') || null;

    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    if (cart && typeof cart.getSectionsToRender === 'function') {
      formData.append(
        'sections',
        cart.getSectionsToRender().map(function (section) {
          return section.id;
        })
      );
      formData.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(button);
    }

    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    var self = this;

    fetch(addUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: formData
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (data.status) {
          throw new Error(data.description || data.message || genericCartError());
        }

        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'kitchen-cabinet-catalog',
            productVariantId: variantId,
            cartData: data
          });
        }

        if (cart && typeof cart.renderContents === 'function') {
          cart.classList.remove('is-empty');
          cart.renderContents(data);
        } else {
          self.updateCartCount(data);
        }

        var title = button.getAttribute('data-product-title') || '';
        self.setStatus(addedMessage(quantity, title), 'success');
      })
      .catch(function (error) {
        console.error('[kitchen-cabinet-catalog]', error);
        self.setStatus(error.message || genericCartError(), 'error');
      })
      .finally(function () {
        self.setLoading(button, false);
      });
  };

  KitchenCabinetCatalog.prototype.updateCartCount = function (data) {
    if (data && data.sections && data.sections['cart-icon-bubble']) {
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        var parsed = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
        var replacement = parsed.querySelector('.shopify-section');
        if (replacement) bubble.innerHTML = replacement.innerHTML;
        return;
      }
    }

    fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js', {
      credentials: 'same-origin'
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
      })
      .catch(function () {
        /* count refresh is best effort only */
      });
  };

  KitchenCabinetCatalog.prototype.setLoading = function (button, isLoading) {
    button.classList.toggle('is-loading', isLoading);
    button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    var spinner = button.querySelector('[data-kcc-spinner]');
    if (spinner) spinner.hidden = !isLoading;
    if (isLoading) {
      button.setAttribute('aria-disabled', 'true');
    } else if (!button.disabled) {
      button.removeAttribute('aria-disabled');
    }
  };

  KitchenCabinetCatalog.prototype.setStatus = function (message, kind) {
    if (!this.status) return;
    clearTimeout(this.statusTimer);

    this.status.textContent = message || '';
    this.status.classList.remove('kcc__status--success', 'kcc__status--error');
    if (kind) this.status.classList.add('kcc__status--' + kind);

    if (message) {
      this.statusTimer = setTimeout(
        function () {
          this.status.textContent = '';
          this.status.classList.remove('kcc__status--success', 'kcc__status--error');
        }.bind(this),
        6000
      );
    }
  };

  /* --- Delegated events ------------------------------------------------- */

  KitchenCabinetCatalog.prototype.onClick = function (event) {
    var filter = event.target.closest('[data-kcc-filter]');
    if (filter && this.root.contains(filter)) {
      this.setFilter(filter.getAttribute('data-kcc-filter'), true);
      return;
    }

    var qtyButton = event.target.closest('[data-kcc-qty-step]');
    if (qtyButton && this.root.contains(qtyButton)) {
      this.stepQuantity(qtyButton);
      return;
    }

    var addButton = event.target.closest('[data-kcc-add]');
    if (addButton && this.root.contains(addButton)) {
      this.addToCart(addButton);
    }
  };

  KitchenCabinetCatalog.prototype.onChange = function (event) {
    if (event.target.matches('[data-kcc-style-select]')) {
      this.setStyle(event.target.value, true);
      return;
    }

    if (event.target.matches('[data-kcc-variant-select]')) {
      this.selectVariant(event.target);
      return;
    }

    if (event.target.matches('[data-kcc-qty-input]')) {
      this.normalizeQuantity(event.target);
    }
  };

  KitchenCabinetCatalog.prototype.onInput = function (event) {
    if (event.target.matches('[data-kcc-search]')) {
      this.search(event.target.value);
      return;
    }

    if (!event.target.matches('[data-kcc-qty-input]')) return;
    // Strip anything that is not a whole number as it is typed.
    var cleaned = event.target.value.replace(/[^\d]/g, '');
    if (cleaned !== event.target.value) event.target.value = cleaned;
  };

  KitchenCabinetCatalog.prototype.search = function (value) {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(
      function () {
        this.searchTerm = (value || '').trim().toLowerCase();
        this.applyFilter();
      }.bind(this),
      150
    );
  };

  /* --- Helpers ---------------------------------------------------------- */

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/["\\\]]/g, '\\$&');
  }

  function genericCartError() {
    return (window.cartStrings && window.cartStrings.error) || 'There was an error adding this item to your cart.';
  }

  function addedMessage(quantity, title) {
    var strings = window.quickOrderListStrings;
    var template = null;

    if (strings) {
      template = quantity === 1 ? strings.itemAdded : strings.itemsAdded;
    }

    if (template && template.indexOf('[quantity]') !== -1) {
      return template.replace('[quantity]', quantity);
    }

    return quantity + ' × ' + title + ' added to your cart.';
  }

  /* --- Boot ------------------------------------------------------------- */

  function init(scope) {
    var container = scope && scope.querySelectorAll ? scope : document;
    var roots = container.querySelectorAll(ROOT_SELECTOR);

    roots.forEach(function (root) {
      if (root.dataset.kccInitialized === 'true') return;
      root.dataset.kccInitialized = 'true';
      root.kccInstance = new KitchenCabinetCatalog(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  // Theme editor: reveal the style panel a merchant is editing.
  document.addEventListener('shopify:block:select', function (event) {
    var panel = event.target;
    if (!panel || !panel.matches || !panel.matches('[data-kcc-style]')) return;

    var root = panel.closest(ROOT_SELECTOR);
    if (!root || !root.kccInstance) return;

    root.kccInstance.setStyle(panel.getAttribute('data-kcc-style'), false);
  });
})();
